import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Edit2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMFOCodes } from "@/hooks/use-mfo-codes";
import { formatCurrency } from "@/utils/typeUtils";
import { useToast } from "@/hooks/use-toast";

const MFOCodeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mfoCodes, isLoading, error } = useMFOCodes();
  const [mfoCode, setMfoCode] = useState<any>(null);

  useEffect(() => {
    if (id && mfoCodes) {
      const code = mfoCodes.find(code => code.id === id);
      if (code) {
        setMfoCode(code);
      } else {
        toast({
          title: "Error",
          description: "MFO code not found",
          variant: "destructive",
        });
        navigate("/dashboard/mfo-codes");
      }
    }
  }, [id, mfoCodes, navigate, toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">Error loading MFO code: {error}</div>
      </div>
    );
  }

  if (!mfoCode) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="mr-2"
          onClick={() => navigate("/dashboard/mfo-codes")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to MFO Codes
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/dashboard/mfo-codes/${id}/edit`)}
        >
          <Edit2 className="h-4 w-4 mr-2" />
          Edit MFO Code
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>MFO Code Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Code</h3>
              <p className="text-lg font-semibold">{mfoCode.code}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Title</h3>
              <p className="text-lg font-semibold">{mfoCode.title}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Budget</h3>
              <p className="text-lg font-semibold">{formatCurrency(mfoCode.budget)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Created</h3>
              <p className="text-lg font-semibold">
                {new Date(mfoCode.created_at || "").toLocaleDateString()}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Last Updated</h3>
              <p className="text-lg font-semibold">
                {new Date(mfoCode.updated_at || "").toLocaleDateString()}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MFOCodeDetail; 